import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { LastSigned, SigningHistory, useSigningHistory } from "@/components/SigningHistory";

function useApprovedForms() {
  const [forms, setForms] = useState([]);
  useEffect(() => {
    api.get("/templates").then((r) => {
      const fams = {};
      r.data.filter((t) => t.status === "active").forEach((t) => { (fams[t.family_id] = fams[t.family_id] || []).push(t); });
      setForms(Object.values(fams).map((g) => g.find((x) => x.language === "en-US") || g[0]).sort((x, y) => x.name.localeCompare(y.name)));
    });
  }, []);
  return forms;
}

export function VisitPacketPlan({ patientId, patientName, facility, provider, sel, setSel }) {
  const forms = useApprovedForms();
  const hist = useSigningHistory(patientId, "");
  useEffect(() => {
    setSel(null);
    if (!forms.length) return undefined;
    let live = true;
    api.get("/default-packets/plan", { params: { patient_id: patientId, facility: facility || "", provider: provider || "" } })
      .then((r) => { if (live) setSel(forms.filter((t) => r.data.needed_families.includes(t.family_id)).map((t) => t.id)); })
      .catch(() => { if (live) setSel([]); });
    return () => { live = false; };
  }, [patientId, facility, provider, forms, setSel]);
  if (!sel) return null;
  const toggle = (id) => (v) => setSel(v ? [...sel, id] : sel.filter((x) => x !== id));
  return (
    <div data-testid="visit-packet-plan">
      <Label>Forms for this visit</Label>
      <div className="mt-1 max-h-52 overflow-y-auto border rounded-md divide-y">
        {forms.map((t) => {
          const info = hist?.last_signed[t.purpose];
          const on = sel.includes(t.id);
          return (
            <div key={t.id} className="px-2.5 py-1.5 text-sm" data-testid={`visit-plan-item-${t.id}`}>
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox checked={on} onCheckedChange={toggle(t.id)} className="mt-0.5" data-testid={`visit-plan-pick-${t.id}`} />
                <span>{t.name} <span className="text-slate-500">v{t.version}</span><LastSigned info={info} formName={t.name} testid={`visit-plan-last-${t.id}`} /></span>
              </label>
              {on && info?.state === "active" && <p className="ml-6 text-xs text-amber-700 flex items-center gap-1" data-testid={`visit-plan-warn-${t.id}`}>
                <AlertTriangle className="w-3 h-3" />Still valid - the patient will be asked to sign it again.</p>}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-500 mt-1" data-testid="visit-plan-summary">{sel.length ? `A packet with ${sel.length} form${sel.length > 1 ? "s" : ""} will be created.` : "No forms ticked - no packet will be created."}</p>
      <SigningHistory history={hist} patientName={patientName} />
    </div>
  );
}
